import { useState } from 'react';
import { useQuery } from '@apollo/client/react';
import { GET_MATCHES } from '../../../../graphql/queries/getMatches';
import { GET_SEASON_COMPETITIONS } from '../../../../graphql/queries/getSeasonCompetitions';
import { GET_SEASONS } from '../../../../graphql/queries/getSeasons';
import type {
  GetMatchesQuery,
  GetMatchesQueryVariables,
  GetSeasonsQuery,
  GetSeasonCompetitionsQueryVariables,
  GetSeasonCompetitionsQuery,
} from '../../../../types/graphql-generated';
import { compareCompetitionsByRankAndName } from '../../components/competitionSorting';

/**
 * State of the season, season competition and match week selection, and the
 * matches of the selected match week. Defaults to the active season and
 * competition and, until another week is picked, to the current match week.
 * Shared by the competition tab and the admin match weeks management page.
 */
export const useMatchWeekSelection = () => {
  const [matchWeekId, setMatchWeekId] = useState<string | undefined>(undefined);
  const [selectedSeasonId, setSelectedSeasonId] = useState<string | undefined>(undefined);
  const [selectedSeasonCompetitionId, setSelectedSeasonCompetitionId] = useState<string | undefined>(undefined);

  const { data: seasonsData, loading: seasonsLoading } = useQuery<GetSeasonsQuery>(GET_SEASONS);
  const seasons = seasonsData?.getSeasons ?? [];

  const selectedOrDefaultSeasonId =
    selectedSeasonId ?? seasons.find((season) => season.active)?.id ?? seasons[0]?.id;

  const { data: seasonCompetitionsData, loading: seasonCompetitionsLoading } =
    useQuery<GetSeasonCompetitionsQuery, GetSeasonCompetitionsQueryVariables>(GET_SEASON_COMPETITIONS, {
      variables: { seasonId: selectedOrDefaultSeasonId },
    });

  const seasonCompetitions = (seasonCompetitionsData?.getSeasonCompetitions ?? [])
    .filter(
      (seasonCompetition) => !selectedOrDefaultSeasonId || seasonCompetition.season.id === selectedOrDefaultSeasonId
    )
    .sort((a, b) => compareCompetitionsByRankAndName(a.competition, b.competition));
  const selectedOrDefaultSeasonCompetitionId =
    selectedSeasonCompetitionId ??
    seasonCompetitions.find((seasonCompetition) => seasonCompetition.active)?.id ??
    seasonCompetitions[0]?.id;

  const matchesQuery = useQuery<GetMatchesQuery, GetMatchesQueryVariables>(GET_MATCHES, {
    variables: { matchWeekId, seasonCompetitionId: selectedOrDefaultSeasonCompetitionId },
  });
  const matchWeekMatches = matchesQuery.data?.getMatches;

  const selectSeason = (seasonId?: string) => {
    setSelectedSeasonId(seasonId);
    setSelectedSeasonCompetitionId(undefined);
    setMatchWeekId(undefined);
  };

  const selectSeasonCompetition = (seasonCompetitionId?: string) => {
    setSelectedSeasonCompetitionId(seasonCompetitionId);
    setMatchWeekId(undefined);
  };

  const goToPreviousMatchWeek = () => {
    if (matchWeekMatches?.previousMatchWeekId) {
      setMatchWeekId(matchWeekMatches.previousMatchWeekId);
    }
  };

  const goToNextMatchWeek = () => {
    if (matchWeekMatches?.nextMatchWeekId) {
      setMatchWeekId(matchWeekMatches.nextMatchWeekId);
    }
  };

  return {
    seasons,
    seasonsLoading,
    selectedSeasonId: selectedOrDefaultSeasonId,
    seasonCompetitions,
    seasonCompetitionsLoading,
    selectedSeasonCompetitionId: selectedOrDefaultSeasonCompetitionId,
    matchesQuery,
    selectSeason,
    selectSeasonCompetition,
    goToPreviousMatchWeek,
    goToNextMatchWeek,
  };
};

export type MatchWeekSelection = ReturnType<typeof useMatchWeekSelection>;
